import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {
    PicklistListItemDto,
    ShipmentPackageDto,
    ShipmentPackageItemDto,
    ShipmentPackageStatusDto,
    ShipmentProcessTypeDto,
    ShipmentTypeDto
} from "../../api/fms";
import {growlStore} from "../GrowlStore";
import {FmsPrinterItem} from "./FmsPrinterStore";
import {isStorageTransferOrInternalProcess} from "./FmsProcessTypeUtil";

class FmsPackageDetailStore {
    get printing(): boolean {
        return this._printing;
    }

    set printing(value: boolean) {
        this._printing = value;
    }

    set executing(value: boolean) {
        this._executing = value;
    }

    get picklists(): PicklistListItemDto[] {
        return this._picklists;
    }

    set picklists(value: PicklistListItemDto[]) {
        this._picklists = value;
    }

    get selectedItem(): ShipmentPackageItemDto | undefined {
        return this._selectedItem;
    }

    set selectedItem(value: ShipmentPackageItemDto | undefined) {
        this._selectedItem = value;
    }

    get data(): ShipmentPackageDto | undefined {
        return this._data;
    }

    private set data(value: ShipmentPackageDto | undefined) {
        this._data = value;
    }

    get loading(): boolean {
        return this._loading;
    }

    private set loading(value: boolean) {
        this._loading = value;
    }

    get id(): string {
        return this._id;
    }

    private set id(value: string) {
        this._id = value;
    }

    get hasReadyItem(): boolean {
        const found = this.data?.items.find(e => e.ready)

        return found !== undefined
    }

    get readyToFinish(): boolean {
        const items = this.data?.items || []
        if (!items.length) {
            return false;
        }

        const openItems = items
            .filter(e => !e.cancelled)
            .filter(e => !e.lost)
            .filter(e => !e.lastScan)
        return openItems.length === 0
    }

    get finished(): boolean {
        return this.data?.status === ShipmentPackageStatusDto.CLOSED || this.data?.status === ShipmentPackageStatusDto.CLOSING;
    }

    get active(): boolean {
        return this.data?.status === ShipmentPackageStatusDto.ACTIVE
    }

    get process(): ShipmentProcessTypeDto {
        return this.data?.processType || ShipmentProcessTypeDto.REGULAR
    }

    private _id: string = ''
    private _loading: boolean = false
    private _printing: boolean = false
    private _executing: boolean = false
    private _data: ShipmentPackageDto | undefined
    private _selectedItem: ShipmentPackageItemDto | undefined
    private _picklists: PicklistListItemDto[] = []

    constructor() {
        makeAutoObservable(this)
    }

    async load(id: string) {
        this.id = id
        return await this.reload()
    }

    async reload() {
        this.loading = true

        const {id} = this

        const result = await catchApi(async () => await api.fms.api.getPackageDetail(id))

        const data = result?.data
        this.data = data

        await this.loadPicklists()

        this.loading = false

        return data
    }

    async printPicklist({
                            documentPrinter,
                            labelPrinter
                        }: { documentPrinter: FmsPrinterItem, labelPrinter: FmsPrinterItem }) {
        const {id, data} = this
        const documentPrinterId = documentPrinter?.id
        const labelPrinterId = labelPrinter?.id || undefined
        const process = data?.processType || ShipmentProcessTypeDto.REGULAR
        const labelPrinterValid = isStorageTransferOrInternalProcess(process) ? true : Boolean(labelPrinter?.id)

        if (documentPrinterId && labelPrinterValid) {
            this.executing = true
            await catchApi(async () => await api.fms.api.createPicklist(id, {
                process: process,
                printerId: documentPrinterId,
                labelPrinterId: labelPrinterId
            }))
            await this.reload()
            this.executing = false
        }
    }

    async finish({
                     printerVlog,
                     printerDocument
                 }: { printerVlog?: FmsPrinterItem, printerDocument: FmsPrinterItem }) {
        const {id} = this
        const labelPrinterId = printerVlog?.id
        const documentPrinterId = printerDocument?.id
        if (labelPrinterId && documentPrinterId) {
            this.executing = true
            await catchApi(async () => await api.fms.api.finishPackage(id, {
                labelPrinterId,
                documentPrinterId
            }))
            await this.reload()
            this.executing = false
        }
    }

    async loadPicklists() {
        const {id} = this
        const result = await catchApi(async () => await api.fms.api.getPicklists(id))

        this.picklists = result?.data.items || []
    }

    async markSelectedItemAsNotLocatable() {
        this.loading = true
        const result = await catchApi(async () => await api.fms.api.itemLost(this.id, this.selectedItem!!.id))

        const data = result?.data
        if (data) {
            this.data = data
            growlStore.add({
                message: "Dokument als nicht auffindbar markiert",
                autoclose: 2000
            })
        }

        this.loading = false
    }

    async markSelectedItemAsCancelled() {
        this.loading = true
        const result = await catchApi(async () => await api.fms.api.itemCancel(this.id, this.selectedItem!!.id))

        const data = result?.data
        if (data) {
            this.data = data
            growlStore.add({
                message: "Dokument storniert",
                autoclose: 2000
            })
        }

        this.loading = false
    }

    async printCoverLetter(vin: string, printerId: string) {
        this.printing = true

        const {id} = this
        const result = await catchApi(async () => api.fms.api.printCoverLetterForVin(id, vin, {
            printerId
        }))

        const data = result?.data
        if (data) {
            this.data = data
            growlStore.add({
                message: "Druckauftrag initialisiert",
                autoclose: 2000
            })
        }

        this.printing = false
    }

    async patchShipmentType(shipmentType: ShipmentTypeDto): Promise<boolean> {
        const {id} = this
        const result = await catchApi(async () => await api.fms.api.updateShipmentType(id, {
            shipmentType
        }))
        return Boolean(result?.data)
    }
}

const fmsPackageDetailStore = new FmsPackageDetailStore()

export {
    fmsPackageDetailStore
}
